"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { HeaderPayload, MenuItem, MenuEntity } from "@/types/menu";
import type { Language } from "@/lib/language-client-utils";

export interface HeaderDataContextValue {
  headerData: HeaderPayload | null;
  isLoading: boolean;
  error: string | null;
  languages: Language[];
  defaultLanguageCode: string | null;
  menus: {
    header: MenuItem[] | null;
    footer: MenuItem[] | null;
    headerTop: MenuItem[] | null;
  };
  logo: string | null;
  socialLinks: Array<{ href: string; icon: string; label: string; title?: string; svg?: string | null }>;
  contactInfo: {
    address?: string;
    email?: string;
    phone?: string;
  };
}

const HeaderDataContext = createContext<HeaderDataContextValue | undefined>(undefined);

export interface HeaderDataProviderProps {
  children: React.ReactNode;
  initialData: HeaderPayload | null;
  initialError?: string | null;
}

export function HeaderDataProvider({ children, initialData, initialError = null }: HeaderDataProviderProps) {
  const [headerData, setHeaderData] = useState<HeaderPayload | null>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);

  // Extract languages from header data
  const languages: Language[] = React.useMemo(() => {
    if (!headerData?.languages?.data) {
      return [];
    }
    return (headerData.languages.data as unknown as Language[]) || [];
  }, [headerData]);

  const defaultLanguageCode = React.useMemo(() => {
    if (!headerData?.languages) {
      return null;
    }
    return (headerData.languages.default_code as string | undefined) || null;
  }, [headerData]);

  // Extract menus from header data
  const menus = React.useMemo(() => {
    if (!headerData?.menus) {
      return {
        header: null,
        footer: null,
        headerTop: null,
      };
    }

    const extractMenuItems = (menuEntity: MenuEntity | undefined): MenuItem[] | null => {
      if (!menuEntity) {
        return null;
      }
      // MenuEntity'den MenuItem[] çıkarma mantığı buraya eklenecek
      // Şimdilik basit bir implementasyon
      const menuData = menuEntity.attributes?.data;
      if (Array.isArray(menuData)) {
        return menuData as MenuItem[];
      }
      if (menuData && typeof menuData === 'object' && 'items' in menuData) {
        return (menuData as { items?: MenuItem[] }).items || null;
      }
      return null;
    };

    return {
      header: extractMenuItems(headerData.menus.header),
      footer: extractMenuItems(headerData.menus.footer),
      headerTop: extractMenuItems(headerData.menus.header_top),
    };
  }, [headerData]);

  // Extract logo from header data
  const logo = React.useMemo(() => {
    if (!headerData?.general_settings) {
      return null;
    }
    const settings = headerData.general_settings as { attributes?: Record<string, unknown> };
    const logoPath = settings?.attributes?.logo as string | undefined;
    return logoPath || null;
  }, [headerData]);

  // Extract social links from header data
  const socialLinks = React.useMemo(() => {
    if (!headerData?.social_medias?.data || !Array.isArray(headerData.social_medias.data)) {
      return [];
    }
    return headerData.social_medias.data.map((item: unknown) => {
      const entity = item as { attributes?: Record<string, unknown> };
      const attrs = entity?.attributes || {};
      return {
        href: (attrs.href as string) || "#",
        icon: (attrs.icon as string) || (attrs.title as string)?.toLowerCase() || "facebook",
        label: (attrs.title as string) || "",
        title: attrs.title as string | undefined,
        svg: attrs.svg as string | null | undefined,
      };
    });
  }, [headerData]);

  // Extract contact info from header data
  const contactInfo = React.useMemo(() => {
    if (!headerData?.contact_info?.attributes) {
      return {};
    }
    const attrs = headerData.contact_info.attributes;
    return {
      address: attrs.address as string | undefined,
      email: attrs.email as string | undefined,
      phone: attrs.phone as string | undefined,
    };
  }, [headerData]);

  const value: HeaderDataContextValue = {
    headerData,
    isLoading,
    error,
    languages,
    defaultLanguageCode,
    menus,
    logo,
    socialLinks,
    contactInfo,
  };

  return <HeaderDataContext.Provider value={value}>{children}</HeaderDataContext.Provider>;
}

export function useHeaderData(): HeaderDataContextValue {
  const context = useContext(HeaderDataContext);
  if (context === undefined) {
    throw new Error("useHeaderData must be used within a HeaderDataProvider");
  }
  return context;
}

